import type { FlowCondition, FlowNode, FlowTrigger } from '@cartly/shared';
import {
  Clock,
  GitBranch,
  MessageCircle,
  MessageSquareText,
  Shuffle,
  Tag,
  Tags,
  type LucideIcon,
} from 'lucide-react';
import type { NodeType } from './graph';

export const TRIGGER_LABEL: Record<FlowTrigger, { title: string; when: string }> = {
  'order.created': { title: 'Order placed', when: 'A new order arrives from a store' },
  'order.paid': { title: 'Order paid', when: 'An order is marked paid' },
  'order.fulfilled': { title: 'Order shipped', when: 'An order is fulfilled' },
  'order.cancelled': { title: 'Order cancelled', when: 'An order is cancelled' },
  'checkout.abandoned': { title: 'Checkout abandoned', when: 'A checkout is left unfinished' },
  'cart.abandoned': { title: 'Cart abandoned', when: 'A cart is left without checking out' },
  'customer.created': { title: 'New customer', when: 'A customer is created' },
  'whatsapp.message_received': {
    title: 'WhatsApp message received',
    when: 'A customer writes to your number',
  },
};

export const NODE_INFO: Record<NodeType, { title: string; icon: LucideIcon; help: string }> = {
  send_whatsapp_template: {
    title: 'Send template',
    icon: MessageCircle,
    help: 'Sends an approved WhatsApp template. Needs the customer’s opt-in.',
  },
  send_whatsapp_text: {
    title: 'Send text',
    icon: MessageSquareText,
    help: 'Free text, only within 24 hours of the customer’s last message.',
  },
  wait: { title: 'Wait', icon: Clock, help: 'Pauses this customer’s run, up to 30 days in total.' },
  condition: {
    title: 'Condition',
    icon: GitBranch,
    help: 'Splits the flow into a yes and a no path.',
  },
  split: {
    title: 'A/B split',
    icon: Shuffle,
    help: 'Sends a share of customers down path A and the rest down path B, to compare messages or timing.',
  },
  add_tag: { title: 'Add tag', icon: Tag, help: 'Tags the customer.' },
  remove_tag: { title: 'Remove tag', icon: Tags, help: 'Removes a tag from the customer.' },
};

export const NODE_ORDER: NodeType[] = [
  'send_whatsapp_template',
  'send_whatsapp_text',
  'wait',
  'condition',
  'split',
  'add_tag',
  'remove_tag',
];

export const CUSTOMER_FIELD_LABEL: Record<string, string> = {
  tag: 'Tag',
  ordersCount: 'Number of orders',
  totalSpent: 'Total spent',
  lastOrder: 'Last order',
  hasPhone: 'Has phone number',
  whatsappConsent: 'WhatsApp consent',
  store: 'Store',
  'order.total': 'This order’s total',
  'order.paymentMethod': 'This order’s payment',
  'order.riskLevel': 'This order’s delivery risk',
  'order.codStatus': 'This order’s COD confirmation',
};

const OP_LABEL: Record<string, string> = {
  has: 'has',
  not_has: 'does not have',
  gte: 'at least',
  lte: 'at most',
  eq: 'exactly',
  within_days: 'within days',
  older_than_days: 'older than days',
  never: 'never ordered',
  is: 'is',
};

export function describeCondition(c: FlowCondition): string {
  const field = CUSTOMER_FIELD_LABEL[c.field] ?? c.field;
  const op = OP_LABEL[c.op] ?? c.op;
  const value = 'value' in c && c.value !== undefined ? ` ${String(c.value)}` : '';
  return `${field} ${op}${value}`;
}

/** One line shown on the node card. */
export function summarize(node: FlowNode): string {
  switch (node.type) {
    case 'send_whatsapp_template':
      return node.templateName || 'Choose a template';
    case 'send_whatsapp_text':
      return node.text || 'Write a message';
    case 'wait':
      return `${node.amount} ${node.amount === 1 ? node.unit.replace(/s$/, '') : node.unit}`;
    case 'condition':
      return node.conditions.map(describeCondition).join(node.match === 'all' ? ' and ' : ' or ');
    case 'split':
      return `A ${node.percentA}% · B ${100 - node.percentA}%`;
    case 'add_tag':
    case 'remove_tag':
      return node.tag || 'Enter a tag';
  }
}

/** Plain-language reading of a run or test outcome. */
export function outcomeLabel(outcome: string | null, status?: string): string {
  if (!outcome) return status === 'waiting' ? 'Waiting' : status === 'running' ? 'Running' : '—';
  if (outcome === 'done') return 'Finished';
  if (outcome === 'flow_archived') return 'Cancelled: the flow was archived';
  if (outcome === 'checkout_completed') return 'Stopped: the customer completed the order';
  if (outcome === 'checkout_active') return 'Stopped: the customer came back to the checkout';
  if (outcome === 'step_limit') return 'Stopped: too many steps';
  if (outcome === 'unknown_node') return 'Stopped: a step points to a missing step';
  if (outcome.startsWith('send_skipped:')) {
    const reason = outcome.slice('send_skipped:'.length);
    const text: Record<string, string> = {
      no_consent: 'no WhatsApp opt-in is recorded',
      opted_out: 'the customer opted out',
      window_closed: 'the 24-hour window is closed',
      no_phone: 'the customer has no phone number',
      template_not_found: 'the template was not found',
      template_not_approved: 'the template is not approved',
      template_params: 'the template parameters do not match',
      daily_limit: 'the daily message limit was reached',
      phone_not_found: 'the WhatsApp number no longer exists',
      account_needs_reauth: 'the WhatsApp account needs a new access token',
      invalid_recipient: 'the phone number is not valid',
    };
    return `Message not sent: ${text[reason] ?? reason.replace(/_/g, ' ')}`;
  }
  return outcome.replace(/_/g, ' ');
}
